import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useSeo } from "../lib/seo";

const SUPPORT_EMAIL = "bookfreely.support@gmail.com";
const LAST_UPDATED = "October 24, 2026";

/**
 * Privacy Policy page.
 * Publicly linked from the footer alongside Terms of Service.
 */
export default function PrivacyPage() {
  useSeo({
    title: "Privacy Policy — BookFreely",
    description:
      "How BookFreely collects, uses, and protects your personal information. Read our full Privacy Policy.",
    path: "/privacy",
  });
  return (
    <div className="App" data-testid="privacy-page">
      <Header hideSearch />
      <main className="max-w-3xl mx-auto px-6 py-12">
        <h1 className="font-display text-4xl sm:text-5xl" style={{ color: "var(--gf-text)" }}>
          Privacy Policy
        </h1>
        <p className="text-sm mt-2" style={{ color: "var(--gf-text-muted)" }}>
          Last updated: {LAST_UPDATED}
        </p>

        <p className="mt-6 text-sm leading-relaxed" style={{ color: "var(--gf-text-soft)" }}>
          BookFreely (&ldquo;BookFreely&rdquo;, &ldquo;we&rdquo;, &ldquo;our&rdquo;, &ldquo;us&rdquo;) operates a service marketplace
          that connects customers with independent service businesses. This Privacy
          Policy explains what personal information we collect, how we use it, who we
          share it with, and the choices you have. By using BookFreely you agree to the
          practices described here.
        </p>

        <Section n="1" title="Who we are &amp; scope">
          <p>
            <b>BookFreely is an online marketplace that connects customers with independent local service providers.</b> Customers browse services, book appointments, and pay securely online; Businesses manage bookings and receive payouts through Stripe Connect. Independent Businesses on the platform are separate data controllers for their own customer relationships (e.g. service delivery, direct communications). This policy covers information we collect through the BookFreely website and payment flows.
          </p>
        </Section>

        <Section n="2" title="Eligibility &amp; age requirements">
          <ul className="list-disc pl-5 space-y-1.5">
            <li><b>Under 13</b> — Not permitted to create any BookFreely account.</li>
            <li><b>Customers 13–17</b> — Permitted only with the permission and supervision of a parent or legal guardian; the guardian&rsquo;s name and email must be on file at signup.</li>
            <li><b>Customers 18+</b> — Permitted independently.</li>
            <li><b>Business owners 18+</b> — Permitted independently.</li>
            <li><b>Business owners 13–17</b> — Permitted only with guardian supervision. The guardian is legally responsible for identity verification, taxes, payouts, and compliance.</li>
          </ul>
          <p className="mt-2">
            We collect date of birth at signup to enforce these gates. See Section 3 (Children&rsquo;s Privacy) for how we protect users under 13.
          </p>
        </Section>

        <Section n="3" title="Children's privacy">
          <p>
            BookFreely is <b>not directed to children under 13</b>, and we do
            not knowingly collect personal information from children under 13.
            Consistent with the U.S. Children&rsquo;s Online Privacy
            Protection Act (COPPA) and similar laws worldwide, if we discover
            or are notified that an account belongs to a child under 13, we
            will:
          </p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Suspend the account immediately.</li>
            <li>Cancel any pending bookings associated with the account.</li>
            <li>Delete the personal information we hold for that account, except records we are legally required to retain (see Section 15 &mdash; Data Retention).</li>
          </ul>
          <p className="mt-2">
            Users aged 13–17 are permitted only with the supervision of a
            parent or legal guardian, whose name and email are collected at
            signup. Parents and guardians who believe their child under 13
            has provided personal information to BookFreely, or who wish to
            review, correct, or delete a minor&rsquo;s information, should
            contact <a href={`mailto:${SUPPORT_EMAIL}`} className="underline">{SUPPORT_EMAIL}</a>.
            We will act promptly on verified requests.
          </p>
        </Section>

        <Section n="4" title="Information we collect">
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Account details: name, preferred name, email, hashed password, account type (customer, business, or BookFreely staff), <b>date of birth</b> (collected at signup to enforce age gates), and — for accounts belonging to users aged 13&ndash;17 — the parent or legal guardian&rsquo;s full name and email.</li>
            <li>Business profile: business name, description, category, contact phone, website, service location, and uploaded profile photo.</li>
            <li>Booking data: services requested, scheduled times, deposit amounts, deposit status, and booking status updates.</li>
            <li>Booking details you provide: your <b>answers to the booking questions the business created</b> for that service (for example vehicle, size, or access details) and any <b>photos you upload with a booking</b>. Private booking photos are available only to you, the business you booked, and authorized BookFreely personnel when access is needed to operate or support the service (see §9).</li>
            <li>Payment metadata received from Stripe: payment status, last-4, card brand, Stripe IDs, amounts. Full card numbers, CVV, bank account numbers, and identity documents submitted for Stripe KYC are handled by Stripe directly — see §6.</li>
            <li>Saved-card authorization records for bookings with a balance: the booking ID, the amounts you authorized, the wording you agreed to, its version, and the timestamp. The card itself is stored by Stripe, not by BookFreely, and is used only for that booking&rsquo;s remaining balance.</li>
            <li>Device &amp; session data: authentication and security data (used to keep you signed in, protect your account, and recognize trusted devices), IP address, user-agent, cookie preferences.</li>
            <li>Messages, reviews, reports, and case data submitted through the platform.</li>
            <li>Optional analytics are <b>currently disabled</b>. No analytics provider loads and no analytics data is collected, even if a visitor accepts analytics cookies. Before enabling analytics in the future, BookFreely will update this policy to accurately explain what is collected and will continue requiring consent.</li>
          </ul>
        </Section>

        <Section n="5" title="How we use your information">
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Provide the marketplace: accounts, bookings, subscriptions, calendar, reminders.</li>
            <li>Payments and payouts: process customer deposits and route funds to businesses via Stripe Connect.</li>
            <li>Safety and moderation: prioritize and investigate reports, run the strike / suspension ladder, prevent fraud (see §7).</li>
            <li>Communication: transactional emails (booking confirmations, reminders, security alerts) and, if you opt in, product updates.</li>
            <li>Legal compliance and accounting.</li>
          </ul>
          <p className="mt-2">We do not sell your personal information.</p>
        </Section>

        <Section n="6" title="Identity verification (Stripe Connect KYC)">
          <p>
            BookFreely relies exclusively on <b>Stripe Connect</b> for Business
            identity verification. Government-issued IDs, business formation
            documents, tax information, and bank details are submitted
            directly to Stripe, verified by Stripe, and stored by Stripe under
            {" "}<a href="https://stripe.com/privacy" target="_blank" rel="noreferrer" className="underline">Stripe&rsquo;s privacy policy</a>.
            <b> BookFreely does not collect, review, or retain government-issued
            IDs separately from Stripe.</b> A Business may become visible and
            bookable once its subscription is active, its account is in good
            standing, and it has at least one published service — before
            Stripe KYC is complete. Until Stripe enables charges and payouts,
            the Business&rsquo;s share of each payment is held by BookFreely as
            a pending transfer; only once Stripe enables charges and payouts
            does the Business receive payouts and display the <b>Verified</b>
            badge. There is no separate manual identity review by BookFreely. The
            same process applies to Businesses that offer in-person services
            — there is no additional approval workflow.
          </p>
        </Section>

        <Section n="7" title="AI moderation (human-supervised)">
          <p>
            Reports of policy violations are automatically summarized and
            severity-scored by an AI moderation assistant (Anthropic&rsquo;s
            Claude, accessed via a third-party integration provider). The AI is a
            triage aid only — <b>it never issues warnings, strikes,
            suspensions, or removals on its own</b>. Every enforcement
            decision is reviewed and finalized by a member of the BookFreely team.
            Report content is transmitted to the AI provider solely for triage
            and is not used for advertising, profiling, or model training on
            our behalf.
          </p>
        </Section>

        <Section n="8" title="Suspension &amp; account enforcement">
          <p>
            BookFreely may suspend, restrict, or terminate any account that
            violates our Terms of Service or Community Guidelines, engages in
            fraud, threatens safety, or otherwise poses risk to the community.
            Enforcement actions and their reasons are recorded in our internal
            audit log so we can respond to appeals and legal requests.
          </p>
        </Section>

        <Section n="9" title="Authorized personnel access">
          <p>
            Only authorized BookFreely personnel may access account or booking
            information, and only when necessary for customer support, fraud
            investigation, dispute resolution, legal compliance, or platform
            security. Authorized access to sensitive records (payment
            metadata, private messages, dispute history, business details)
            may be recorded for security, support, compliance, and
            accountability. We do not permit casual browsing of user data.
          </p>
        </Section>

        <Section n="10" title="Payment processing (Stripe)">
          <p>
            All payments and payouts are processed by Stripe, Inc. and by
            Stripe Connect connected accounts owned by each Business. Card
            numbers, bank details, and identity documents submitted for
            Stripe KYC are handled directly by Stripe under their terms and
            privacy policy (<a href="https://stripe.com/privacy" target="_blank" rel="noreferrer" className="underline">stripe.com/privacy</a>).
            BookFreely receives only the operational metadata described in
            §4.
          </p>
        </Section>

        <Section n="11" title="Email delivery">
          <p>
            Transactional emails are delivered via our email service provider.
            Recipient email address, subject, and message content are shared
            with the provider solely for delivery.
          </p>
        </Section>

        <Section n="12" title="Cookies, sessions, and Trusted Devices">
          <p>
            We use a session token for authentication and an optional
            &ldquo;Trusted Device&rdquo; token so signed-in users on a
            remembered device can skip step-up verification for up to 30 days.
            You can review and revoke trusted devices from your account
            settings. Optional analytics and marketing cookies are controlled
            from the &ldquo;Cookie preferences&rdquo; link in the footer.
          </p>
        </Section>

        <Section n="13" title="Sharing your information">
          <ul className="list-disc pl-5 space-y-1.5">
            <li>With the Business you book from: your name and contact phone / email (revealed only after the booking is confirmed) so they can prepare for and, where relevant, contact you about the appointment.</li>
            <li>With our sub-processors: Stripe (payments and KYC), our transactional email provider, our AI moderation provider (Anthropic Claude), and our hosting provider.</li>
            <li>When legally required (subpoena, safety emergency, fraud investigation).</li>
            <li>In connection with a business transfer (merger or acquisition) with continued protection of your information.</li>
          </ul>
        </Section>

        <Section n="14" title="Your rights and choices">
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Access, correct, or delete your account data from your dashboard.</li>
            <li>Export your data: Businesses can download a full account export from Business Dashboard → Account.</li>
            <li>Delete your account: Businesses use the deletion flow (30-day grace period). Customers can request deletion by emailing <a href={`mailto:${SUPPORT_EMAIL}`} className="underline">{SUPPORT_EMAIL}</a>.</li>
            <li>Revoke trusted devices, opt out of marketing emails, adjust cookie preferences at any time.</li>
            <li>Applicable law may give you additional rights (GDPR, CCPA) — contact us to exercise them.</li>
          </ul>
        </Section>

        <Section n="15" title="Data retention">
          <p>
            We retain personal information only for as long as needed to
            provide the platform, comply with our legal obligations, resolve
            disputes, and enforce our agreements. Actual retention periods
            depend on the type of data and the purpose it was collected for:
          </p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li><b>Active accounts</b> — retained while your account remains active.</li>
            <li><b>Deleted customer accounts</b> — profile data is deleted or anonymized promptly after a verified deletion request, except records we must retain for legal, tax, accounting, fraud-prevention, or Stripe reconciliation purposes.</li>
            <li><b>Deleted business accounts</b> — profile is hidden immediately and enters a 30-day grace period. After the grace period, profile data is deleted or anonymized, subject to the same legal exceptions above.</li>
            <li><b>Booking and payment records</b> — retained for the period required by applicable tax, accounting, consumer-protection, and anti-money-laundering laws (typically up to <b>7 years</b>).</li>
            <li><b>Report, case, and audit-log data</b> — retained for the period necessary to defend against legal claims, respond to appeals, and demonstrate compliance (typically up to <b>7 years</b>).</li>
            <li><b>Marketing preferences and unsubscribe records</b> — retained indefinitely to honor your choices.</li>
            <li><b>Backups</b> — deleted data may persist in routine service backups for a limited window until those backups roll over.</li>
          </ul>
          <p className="mt-2">
            When data is no longer needed, we securely delete or anonymize it. Where anonymized, the resulting data is no longer personal information and may be used for analytics.
          </p>
        </Section>

        <Section n="16" title="Data security">
          <p>
            We take reasonable steps to protect your data, including
            encryption in transit (HTTPS), salted password hashing, step-up
            verification for sensitive actions, and access controls on
            administrative functions. No system is perfectly secure, so please
            use a strong, unique password and enable step-up verification
            when prompted.
          </p>
        </Section>

        <Section n="17" title="International transfers">
          <p>
            Your data may be stored or processed by sub-processors in
            countries other than your own. Where cross-border transfers occur,
            we work with sub-processors who commit to appropriate safeguards
            for such transfers.
          </p>
        </Section>

        <Section n="18" title="Changes to this policy">
          <p>
            We may update this policy. Material changes will be announced by
            email or on-platform notice. The &ldquo;Last updated&rdquo; date at the
            top always reflects the current version.
          </p>
        </Section>

        <Section n="19" title="Contact">
          <p>
            Questions or requests: <a href={`mailto:${SUPPORT_EMAIL}`} className="underline">{SUPPORT_EMAIL}</a>.
          </p>
        </Section>
      </main>
      <Footer />
    </div>
  );
}

function Section({ n, title, children }) {
  return (
    <section className="mt-8" data-testid={`privacy-section-${n}`}>
      <h2 className="font-display text-xl sm:text-2xl" style={{ color: "var(--gf-text)" }}>
        {n}. {title}
      </h2>
      <div className="mt-2 text-sm leading-relaxed space-y-2" style={{ color: "var(--gf-text-soft)" }}>
        {children}
      </div>
    </section>
  );
}
